import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Building2, GraduationCap, MapPin, Search, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import JobCard from "../components/JobCard";
import LatestBlogs from "../components/LatestBlogs";
import { useSiteContent } from "../content/SiteContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const quickFilters = ["All opportunities", "ITI", "Diploma", "12th Pass", "NAPS", "NATS"];

export default function HomePage() {
  const [jobs, setJobs] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All opportunities");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const { content } = useSiteContent();

  useEffect(() => {
    fetch(`${API}/jobs`)
      .then((response) => response.json())
      .then((data) => setJobs(data))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredJobs = useMemo(() => jobs.filter((job) => {
    const searchable = `${job.title} ${job.company} ${job.location} ${job.education} ${job.scheme} ${job.trade}`.toLowerCase();
    const filterMatches = activeFilter === "All opportunities" || searchable.includes(activeFilter.toLowerCase());
    return filterMatches && searchable.includes(searchTerm.toLowerCase());
  }), [activeFilter, jobs, searchTerm]);

  return (
    <main data-testid="home-page">
      <section className="hero-section" style={{ backgroundImage: `url("${content.hero_background_url}")` }} data-testid="hero-section">
        <div className="hero-overlay" />
        <div className="page-width hero-content">
          <span className="hero-kicker" data-testid="hero-kicker"><Sparkles size={15} /> {content.hero_kicker}</span>
          <h1 data-testid="hero-title">{content.hero_heading}<br /><em>{content.hero_highlight}</em></h1>
          <p data-testid="hero-description">{content.hero_description}</p>
          <div className="hero-actions">
            <a href="#opportunities" className="hero-primary" data-testid="hero-find-jobs-button">{content.find_jobs_label} <ArrowRight size={18} /></a>
            <Link to="/employers" className="hero-secondary" data-testid="hero-company-link">{content.hiring_label}</Link>
          </div>
          <div className="trust-row" data-testid="trust-row"><span><ShieldCheck size={18} /> {content.trust_badge_one}</span><span><GraduationCap size={18} /> {content.trust_badge_two}</span></div>
        </div>
      </section>

      <section className="quick-filter-section" data-testid="quick-filter-section">
        <div className="page-width filter-content">
          <div className="search-box"><Search size={20} /><input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder={content.search_placeholder} data-testid="job-search-input" /></div>
          <div className="filter-chips" data-testid="job-filter-chips">
            {quickFilters.map((filter) => <button key={filter} type="button" className={activeFilter === filter ? "filter-chip active" : "filter-chip"} onClick={() => setActiveFilter(filter)} data-testid={`filter-${filter.toLowerCase().replaceAll(" ", "-")}`}>{filter}</button>)}
          </div>
        </div>
      </section>

      <section className="page-width opportunities-section" id="opportunities" data-testid="opportunities-section">
        <div className="section-heading">
          <div><span className="eyebrow">{content.opportunities_eyebrow}</span><h2>{content.opportunities_heading}</h2></div>
          <p data-testid="job-results-count">{loading ? "Loading opportunities…" : `${filteredJobs.length} roles matched for you`}</p>
        </div>
        {loading ? <div className="job-grid" data-testid="job-skeletons">{[1, 2, 3].map((number) => <div className="job-skeleton" key={number} />)}</div> : (
          <div className="job-grid" data-testid="job-results-grid">
            {filteredJobs.map((job, index) => <JobCard job={job} index={index} key={job.id} />)}
          </div>
        )}
        {!loading && filteredJobs.length === 0 && <div className="empty-state" data-testid="job-empty-state"><Search size={30} /><h3>No opportunities found</h3><p>Try a different trade, location, or qualification.</p></div>}
      </section>

      <section className="category-section" data-testid="category-section">
        <div className="page-width"><div className="section-heading narrow-heading"><div><span className="eyebrow">{content.pathways_eyebrow}</span><h2>{content.pathways_heading}</h2></div></div><div className="category-grid">{content.categories.map((category) => <button type="button" className="category-tile" key={category.label} onClick={() => { setSearchTerm(category.label); document.getElementById("opportunities").scrollIntoView({ behavior: "smooth" }); }} data-testid={`category-${category.label.toLowerCase()}`}><img src={category.image_url} alt={`${category.label} field`} data-testid={`category-image-${category.label.toLowerCase()}`} /><span>{category.label}</span><small>{category.hindi_label}</small></button>)}</div></div>
      </section>

      <section className="employer-banner" data-testid="employer-banner"><div className="page-width employer-banner-content"><div><span className="eyebrow inverse">{content.partner_eyebrow}</span><h2>{content.partner_heading}</h2><p>{content.partner_description}</p></div><Link to="/employers" className="employer-banner-link" data-testid="employer-banner-link"><Building2 size={19} /> {content.partner_button_label}</Link></div></section>
      <LatestBlogs />
    </main>
  );
}